import React, {useEffect, useState} from "react";
import {SocialAccount, SocialPlatform, SocialPlatformNames} from "../../../types/socialTypes";
import ErrorSnackBar from "../../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import {LoadError} from "../../../types/generic";
import {
    createSocialAccount,
    deleteSocialAccount,
    getGroupedAccounts, loginWithFacebook, loginWithGoogle, loginWithInstagram,
    updateSocialAccount
} from "../../../queries/socialQueries";
import Box from "@mui/material/Box";
import {Link, Typography} from "@mui/material";
import StyledCollapse from "../../CommonComponents/StyledCollapse/StyledCollapse";
import SocialAccountForm from "./SocialAccountForm";
import TransparentButton from "../../CommonComponents/TransparentButton/TransparentButton";
import {useTheme} from "../../../contexts/theme";
import DailyMotionIcon from "../../CommonComponents/Icons/Social/Dailymotion"
import InstagramIcon from "../../CommonComponents/Icons/Social/Instagram"
import FacebookIcon from "../../CommonComponents/Icons/Social/Facebook"
import YoutubeIcon from "../../CommonComponents/Icons/Social/Youtube"
import TikTokIcon from "../../../assets/images/Social/tiktok.svg";
import {socialFormFields} from "../../../utils/constants";
import StyledConfirmationDialog from "../../CommonComponents/StyledConfirmitationDialog/StyledConfirmationDialog";
import socialAccountForm from "./SocialAccountForm";

interface SocialAccountProps {
    handleErrorMessage : (error: boolean, message: string) => void;
}

const socialInstructions = {
    [SocialPlatformNames.dailyMotion]: <Link href="https://developers.dailymotion.com/guides/" target="_blank"> documentation</Link>,
    [SocialPlatformNames.instagram]: <Link href="https://mui.com/" target="_blank"> documentation</Link>,
    [SocialPlatformNames.facebook]: <Link href="https://mui.com/" target="_blank"> documentation</Link>,
    [SocialPlatformNames.youtube]: <Link href="https://mui.com/" target="_blank"> documentation</Link>,
    [SocialPlatformNames.tiktok]: <Link href="https://mui.com/" target="_blank"> documentation</Link>
}

const defaultSocialAccount: SocialAccount = {
    id: "",
    name: "",
    account_id: "",
    business_id: "",
    app_id: "",
    page_id: "",
    page_access_token: "",
    client_id: "",
    client_secret: "",
    is_token_valid: false,
    auth_code: "",
    redirect_uri: "",
    social_platform: {
        id: "",
        name: SocialPlatformNames.dailyMotion,
        deletion_support: false,
        social_contents_supported: []
    },
    token: ""
};

const SocialAccountSection: React.FC<SocialAccountProps> = ({ handleErrorMessage }) => {
    const theme = useTheme();

    const socialIcons = {
        [SocialPlatformNames.facebook]: <FacebookIcon color={theme.palette.white}/>,
        [SocialPlatformNames.instagram]: <InstagramIcon color={theme.palette.white}/>,
        [SocialPlatformNames.tiktok]: <img src={TikTokIcon} style={{width: '25px', height: '25px', marginRight: '5px', color: 'white',}} /> ,
        [SocialPlatformNames.youtube]: <YoutubeIcon color={theme.palette.white} />,
        [SocialPlatformNames.dailyMotion]: <DailyMotionIcon color={theme.palette.white} />
    }

    const [socialPlatforms, setSocialPlatform] = useState<SocialPlatform[]>([])
    const [loadError, setLoadError] = useState<LoadError>({message:'', error: false});
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const [targetSocialAccount, setTargetSocialAccount] = useState<SocialAccount>();
    const [targetSocialPlatform, setTargetSocialPlatform] = useState<SocialPlatform>();

    async function loadAccounts() {
        try{
            const socialPlatforms = await getGroupedAccounts()
            for (const socialPlatform of socialPlatforms){
                if (socialPlatform.accounts){
                    for (const socialAccount of socialPlatform.accounts){
                        socialAccount.mode = "readonly"
                    }
                }
            }
            setSocialPlatform(socialPlatforms);

        }catch(err){
            console.error(err);
            setLoadError({message:'Problems loading the socials accounts', error: true});
        }
    }

    function blank_secret_fields(socialPlatform: SocialPlatform, socialAccount: SocialAccount){
        const secretSocialAccountFields = socialFormFields[socialPlatform.name].filter(form => form.secret)
        for (const secretField of secretSocialAccountFields){
            (socialAccount[secretField.name as keyof SocialAccount] as any) = "";
        }
    }

    function onChangeFormMode(socialPlatformIndex: number, socialAccountIndex: number, mode: 'readonly' | 'updatable'){
        const tempSocialPlatforms = [...socialPlatforms]
        const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
        if (socialAccounts){
            const socialAccount = socialAccounts[socialAccountIndex] as any
            socialAccount.mode = mode
            blank_secret_fields(tempSocialPlatforms[socialPlatformIndex], socialAccounts[socialAccountIndex])
            setSocialPlatform(tempSocialPlatforms)
        }
    }

    useEffect(() => {
        loadAccounts()
    }, []);

    function handleFieldChange(fieldName: string, value: any, socialPlatformIndex: number, socialAccountIndex: number){
        const tempSocialPlatforms = [...socialPlatforms]
        const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
        if (socialAccounts){
            const socialAccount = socialAccounts[socialAccountIndex] as any
            socialAccount[fieldName] = value
            setSocialPlatform(tempSocialPlatforms)
        }
    }

    async function handleAddAccount(socialPlatformIndex: number){
        const tempSocialPlatforms = [...socialPlatforms]
        const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
        const socialPlatform = tempSocialPlatforms[socialPlatformIndex]
        if (socialAccounts && (tempSocialPlatforms[socialPlatformIndex].name !== SocialPlatformNames.youtube && tempSocialPlatforms[socialPlatformIndex].name !== SocialPlatformNames.instagram && tempSocialPlatforms[socialPlatformIndex].name !== SocialPlatformNames.facebook)){
            const socialAccount = JSON.parse(JSON.stringify(defaultSocialAccount)) as SocialAccount
            socialAccount.social_platform.id = socialPlatform.id
            socialAccount.social_platform.name = socialPlatform.name
            socialAccount.mode = "updatable"
            socialAccounts.unshift(socialAccount)
            setSocialPlatform(tempSocialPlatforms)
        }
        else if (socialAccounts && tempSocialPlatforms[socialPlatformIndex].name === SocialPlatformNames.youtube){
            try{
                const redirectUrl = await loginWithGoogle();
                window.location.replace(redirectUrl);
            }catch (e) {
                console.error(e)
                setLoadError({message:'Error occurred while logging in', error: true});
            }

        }
        else if (socialAccounts && tempSocialPlatforms[socialPlatformIndex].name === SocialPlatformNames.instagram){
            try{
                const redirectUrl = await loginWithInstagram()
                window.location.replace(redirectUrl);
            }catch (e) {
                console.error(e)
                setLoadError({message:'Error occurred while logging in', error: true});
            }

        }
        else if (socialAccounts && tempSocialPlatforms[socialPlatformIndex].name === SocialPlatformNames.facebook){
            try{
                const redirectUrl = await loginWithFacebook()
                window.location.replace(redirectUrl);
            }catch (e) {
                console.error(e)
                setLoadError({message:'Error occurred while logging in', error: true});
            }

        }
    }

    async function handleDeleteAccount(socialPlatformIndex:number, socialAccountIndex:number){
        setTargetSocialPlatform(socialPlatforms[socialPlatformIndex])
        const socialAccounts = socialPlatforms[socialPlatformIndex].accounts
        if (socialAccounts){
            setTargetSocialAccount(socialAccounts[socialAccountIndex])
            setDeletePopupOpen(true);
        }
    }

    async function handleUpdateAccount(socialPlatformIndex: number, socialAccountIndex: number) {

            try {
                const tempSocialPlatforms = [...socialPlatforms]
                const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
                if(socialAccounts) {
                     await updateSocialAccount(socialAccounts[socialAccountIndex].id, socialAccounts[socialAccountIndex]);
                    handleErrorMessage(true, "Acccount Updated");
                }
            } catch (error) {
                handleErrorMessage(true, "Acccount Not Updated");
            }
    }

    async function handleCreateAccount(socialPlatformIndex: number, socialAccountIndex: number) {
        try {
            const tempSocialPlatforms = [...socialPlatforms]
            const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
            if(socialAccounts) {

                const socialAccountId = await createSocialAccount(socialAccounts[socialAccountIndex]);

                const socialAccount = socialAccounts[socialAccountIndex]
                socialAccount.id = socialAccountId.id
                socialAccount.mode = 'readonly'
                blank_secret_fields(tempSocialPlatforms[socialPlatformIndex], socialAccounts[socialAccountIndex])
                setSocialPlatform(tempSocialPlatforms)
                handleErrorMessage(true, "Account created" )
            }
        } catch (errorCreateAccount) {
            handleErrorMessage(true, "Account creation failed" )
        }
    }

    async function handleDeletionConfirm(){
        try{
            if (targetSocialAccount && targetSocialPlatform){
                try{
                    const tempSocialPlatforms = [...socialPlatforms]
                    const socialPlatform = tempSocialPlatforms.find(sc => sc.id === targetSocialPlatform.id)
                    if (socialPlatform){
                        const socialAccounts = socialPlatform.accounts as SocialAccount[]
                        const socialAccountIndex = socialAccounts.findIndex(sa => sa.id === targetSocialAccount.id)
                        await deleteSocialAccount(targetSocialAccount.id)
                        socialAccounts.splice(socialAccountIndex, 1)
                        setSocialPlatform(tempSocialPlatforms)
                        handleErrorMessage(true, "Account Deleted");
                    }
                }catch (e) {
                    handleErrorMessage(true, "Account Not Deleted");
                }
            }
        }catch (e) {

        }
    }

    return (<Box>
        <ErrorSnackBar autoHideDuration={5000} open={loadError.error} setClose={() => setLoadError({message:'', error: false})} errorMessage={loadError.message}/>
        <StyledConfirmationDialog
            onClose={() => setDeletePopupOpen(false)}
            onConfirm={handleDeletionConfirm}
            onDeny={() => setDeletePopupOpen(false)}
            message={`Are you sure to delete ${targetSocialAccount && targetSocialAccount.name} ${targetSocialPlatform && targetSocialPlatform.name} account ?`}
            open={deletePopupOpen}
        />
        <Typography component="h1" variant="h5" color={theme.palette.white}>Social Accounts</Typography>
        <Box>
            {socialPlatforms.map((socialPlatform, socialPlatformIndex) => (
                <StyledCollapse title={socialPlatform.name} icon={socialIcons[socialPlatform.name]} >
                        <TransparentButton text={socialPlatform.name === SocialPlatformNames.youtube ? "Log In with Google" : socialPlatform.name === SocialPlatformNames.instagram ? "Log In with Instagram" : socialPlatform.name === SocialPlatformNames.facebook ? "Log in With Facebook" : "Add Account"} onClick={() => handleAddAccount(socialPlatformIndex)}></TransparentButton>
                    {socialPlatform.accounts && socialPlatform.accounts.map((socialAccount, socialAccountIndex) => {
                        const refreshDate = new Date(socialAccount.refresh_time!);
                        const now = new Date();

                        return (
                            <Box key={socialAccountIndex} sx={{ marginTop: theme.spacing.md }}>
                                <SocialAccountForm
                                    socialPlatform={socialPlatform.name}
                                    socialAccount={socialAccount}
                                    onFieldChange={(field, value) => handleFieldChange(field, value, socialPlatformIndex, socialAccountIndex)}
                                    onDelete={() => handleDeleteAccount(socialPlatformIndex, socialAccountIndex)}
                                    onCreate={() => handleCreateAccount(socialPlatformIndex, socialAccountIndex)}
                                    onUpdate={() => handleUpdateAccount(socialPlatformIndex, socialAccountIndex)}
                                    onChangeMode={(mode) => onChangeFormMode(socialPlatformIndex, socialAccountIndex, mode)}
                                    mode={socialAccount.mode ? socialAccount.mode : 'readonly'}
                                />

                                {socialPlatform.name === SocialPlatformNames.facebook && refreshDate <= now && (
                                    <Typography color={theme.palette.orange}>
                                        Attention! You must log in again as your token has expired.
                                    </Typography>
                                )}

                                {socialPlatform.name === SocialPlatformNames.youtube && socialAccount.token && (
                                    <Typography color={theme.palette.orange}>
                                        Attention! You must log in again as your token has expired.
                                    </Typography>
                                )}

                            </Box>
                        );
                    })}

                    <Box sx={{ marginTop: theme.spacing.md }}>
                        {/* Caso 1: nessun account */}
                        {(!socialPlatform.accounts || socialPlatform.accounts.length === 0) && (
                            <Typography color={theme.palette.white}>
                                No account for this social
                            </Typography>
                        )}

                        {/* Caso 2: dailyMotion */}
                        {socialPlatform.accounts &&
                            socialPlatform.accounts.length > 0 &&
                            socialPlatform.name === SocialPlatformNames.dailyMotion && (
                                <Typography color={theme.palette.white}>
                                    {`You don't know how to retrieve this ${socialPlatform.name} info? Try to consult this `}
                                    {socialInstructions[socialPlatform.name]}
                                </Typography>
                            )}

                    </Box>

                </StyledCollapse>
            ))}
        </Box>
    </Box>)
}

export default SocialAccountSection

